import * as Sentry from '@sentry/react';
import React, {createContext, useCallback, useContext, useMemo} from 'react';
import {SettingType} from 'src/services/analytics/workspaceSettingsEvents';
import {SettingOverrideCounts} from 'src/state/views/workspaceSettings/settingOverrideUtils';

import {
  SemanticLegendsQueryDocument,
  useCreateSemanticLegendMutation,
  useUpdateSemanticLegendMutation,
} from '../../../../generated/graphql';
import {useSelector} from '../../../../state/hooks';
import {useViewer} from '../../../../state/viewer/hooks';
import {usePart, useViewAction} from '../../../../state/views/hooks';
import * as WorkspaceSettingsActions from '../../../../state/views/workspaceSettings/actions';
import {useWorkspaceRefsContext} from '../../../Workspace/WorkspaceRefsContext';
import {useIsSaveWorkspaceAllowed} from '../../../WorkspaceBanners/useWorkspaceBanners';
import {ClearSettingFnType} from '../hooks/useSettingOverrides';
import {RunColorOptions, SemanticLegendSettings, YAxisValues} from '../types';
import {DEFAULT_SEMANTIC_LEGEND_SETTINGS} from './SemanticLegendDefaults';
import {parseBucketsFromSemanticLegendSettings} from './utils/parseBuckets';
import {recalculateSemanticLegendBuckets} from './utils/recalculateSemanticLegendBuckets';
import {useSemanticLegendSettingsQueryProps} from './utils/useSemanticLegendSettingsQueryProps';
export type SemanticLegendSettingsContextType = {
  clearSetting: ClearSettingFnType;
  /**
   * These are the values used in the settings UX. This is the resolved value between different levels of settings.
   */
  semanticLegendRunColorOptions?: RunColorOptions;
  semanticLegendSettings?: SemanticLegendSettings;
  /**
   * These are the settings for the specified settingLevel. This should match what's in the view spec, but not necessarily
   * be the value that is displayed depending on the settingLevel
   */
  settingLevel: 'workspace';
  settingOverrideCounts: SettingOverrideCounts;
  trackSemanticLegendSettings: (
    setting: SettingType,
    value: string | undefined
  ) => void;
  updateSemanticLegendRunColorOptions?: (
    runColorOptions: RunColorOptions
  ) => void;
  updateSemanticLegendSettings?: (
    settings: Partial<SemanticLegendSettings>
  ) => void;
  createSemanticLegendSettings?: () => void;
  createOrUpdateSemanticLegendSettings?: (
    newSettings: Partial<SemanticLegendSettings>
  ) => void;
};

export const SemanticLegendSettingsContext = createContext<
  SemanticLegendSettingsContextType | undefined
>(undefined);

type SemanticLegendSettingsProps = SemanticLegendSettingsContextType & {
  children: React.ReactNode;
};

export const SemanticLegendSettingsContextProvider = ({
  children,
  clearSetting,
  settingLevel,
  settingOverrideCounts,
  trackSemanticLegendSettings,
}: SemanticLegendSettingsProps) => {
  const {workspaceSettingsRef, viewRef} = useWorkspaceRefsContext();
  const workspaceSettings = usePart(workspaceSettingsRef);
  const view = useSelector(state => state.views.views[viewRef.viewID]);
  const viewer = useViewer();
  const canSaveWorkspace = useIsSaveWorkspaceAllowed(view, viewer);

  const semanticLegendRunColorOptions =
    workspaceSettings?.semanticLegendRunColorOptions ??
    RunColorOptions.OriginalProjectColors;

  const updateSemanticLegendRunColorOptions = useViewAction(
    workspaceSettingsRef,
    WorkspaceSettingsActions.updateSemanticLegendRunColorOptions
  );

  const [updateSemanticLegendMutation] = useUpdateSemanticLegendMutation();

  const updateSemanticLegendSettings = useCallback(
    (semanticLegendSettingsData: SemanticLegendSettings) => {
      // if the user is not allowed to save the workspace, we don't want to update the semantic legend settings
      if (!canSaveWorkspace) {
        return;
      }

      if (
        !semanticLegendSettingsData ||
        semanticLegendSettingsData.metric ===
          DEFAULT_SEMANTIC_LEGEND_SETTINGS.metric ||
        !view?.id
      ) {
        return;
      }

      if (semanticLegendSettingsData.shouldRecalculateBuckets) {
        semanticLegendSettingsData.buckets = recalculateSemanticLegendBuckets(
          semanticLegendSettingsData?.numberOfBuckets ?? 0,
          semanticLegendSettingsData?.metricStatistics ?? {},
          semanticLegendSettingsData?.yAxisValue ?? YAxisValues.Min
        );
        semanticLegendSettingsData.shouldRecalculateBuckets = false;
      }
      // if we have any bucket customizations, we need to update the buckets before updating the semantic legend settings
      const {customColors, bucketUpperBounds} =
        parseBucketsFromSemanticLegendSettings(semanticLegendSettingsData);

      updateSemanticLegendMutation({
        variables: {
          id: view?.id,
          currentMetric: semanticLegendSettingsData.metric ?? '',
          metricName: semanticLegendSettingsData.metric,
          yValue: semanticLegendSettingsData.yAxisValue ?? YAxisValues.Min,
          bucketUpperBounds: bucketUpperBounds?.map(bucket => bucket ?? 0),
          excludedRunColor: semanticLegendSettingsData.excludedRunColor,
          customColors: customColors,
        },
        refetchQueries: [
          {
            query: SemanticLegendsQueryDocument,
            variables: {id: view?.id ?? ''},
          },
        ],
      });
    },
    [view?.id, updateSemanticLegendMutation, canSaveWorkspace]
  );

  const {semanticLegendSettingsData, semanticLegendSettingsLoading} =
    useSemanticLegendSettingsQueryProps({
      entityName: view?.project?.entityName ?? '',
      projectName: view?.project?.name ?? '',
      viewID: view?.id ?? '',
      updateSemanticLegendSettings: updateSemanticLegendSettings,
    });

  const [createSemanticLegendMutation] = useCreateSemanticLegendMutation({
    context: {
      propagateErrors: true,
    },
  });

  const createSemanticLegendSettings = useCallback(() => {
    if (!view?.id) {
      console.error('No view ID found');
      return;
    }

    // if the user is not allowed to save the workspace, we don't want to update the semantic legend settings
    if (!canSaveWorkspace) {
      return;
    }

    try {
      createSemanticLegendMutation({
        variables: {
          id: view?.id,
          metricName: DEFAULT_SEMANTIC_LEGEND_SETTINGS.metric ?? '',
          yValue:
            DEFAULT_SEMANTIC_LEGEND_SETTINGS.yAxisValue?.toString() ??
            YAxisValues.Min,
          bucketUpperBounds:
            DEFAULT_SEMANTIC_LEGEND_SETTINGS.buckets?.map(
              bucket => bucket.bucketMax ?? 0
            ) ?? [],
          excludedRunColor: DEFAULT_SEMANTIC_LEGEND_SETTINGS.excludedRunColor,
        },
      });
    } catch (error) {
      console.error('Error creating semantic legend settings', error);
    }
  }, [view?.id, createSemanticLegendMutation, canSaveWorkspace]);

  const createOrUpdateSemanticLegendSettings = useCallback(
    (newSettings: Partial<SemanticLegendSettings>) => {
      if (!semanticLegendSettingsLoading) {
        // if there is no semantic legend settings data or if we've just returned the default settings after we've fetched it, create the default settings
        if (
          !semanticLegendSettingsData ||
          semanticLegendSettingsData?.metric ===
            DEFAULT_SEMANTIC_LEGEND_SETTINGS.metric
        ) {
          createSemanticLegendSettings?.();
        }
        updateSemanticLegendSettings?.(newSettings);
      }
    },
    [
      semanticLegendSettingsData,
      createSemanticLegendSettings,
      updateSemanticLegendSettings,
      semanticLegendSettingsLoading,
    ]
  );

  const value = useMemo(
    () => ({
      clearSetting,
      settingLevel,
      settingOverrideCounts,
      trackSemanticLegendSettings,
      semanticLegendSettings: semanticLegendSettingsData,
      semanticLegendRunColorOptions,
      updateSemanticLegendRunColorOptions,
      updateSemanticLegendSettings,
      createSemanticLegendSettings,
      createOrUpdateSemanticLegendSettings,
    }),
    [
      clearSetting,
      settingLevel,
      settingOverrideCounts,
      trackSemanticLegendSettings,
      semanticLegendSettingsData,
      semanticLegendRunColorOptions,
      updateSemanticLegendRunColorOptions,
      updateSemanticLegendSettings,
      createSemanticLegendSettings,
      createOrUpdateSemanticLegendSettings,
    ]
  );

  return (
    <SemanticLegendSettingsContext.Provider value={value}>
      {children}
    </SemanticLegendSettingsContext.Provider>
  );
};

export const useSemanticLegendSettingsContext =
  (): SemanticLegendSettingsContextType => {
    const value = useContext(SemanticLegendSettingsContext);

    if (value == null) {
      Sentry.captureException(
        'useSemanticLegendSettingsContext must be used within a SemanticLegendSettingsContextProvider'
      );
      return {
        clearSetting: () => {},
        semanticLegendRunColorOptions: RunColorOptions.OriginalProjectColors,
        semanticLegendSettings: DEFAULT_SEMANTIC_LEGEND_SETTINGS,
        settingLevel: 'workspace',
        settingOverrideCounts: {},
        trackSemanticLegendSettings: () => {},
        updateSemanticLegendRunColorOptions: () => {},
        updateSemanticLegendSettings: () => {},
        createSemanticLegendSettings: () => {},
      };
    }

    return value;
  };
